import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  ButtonBase,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined';
import { keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import WaiterDeviceConfig from '../components/WaiterDeviceConfig';
import PageMenuButton from '../components/PageMenuButton';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { getCatalogStorageSession, isCatalogStorageSessionCurrent } from '../utils/catalogSessionStorage';
import {
  clearWaiterDeviceSession,
  getWaiterDeviceSession,
  waiterDeviceFetch,
} from '../utils/waiterDeviceApi';
import {
  adaptWaiterDeviceCatalog,
  buildWaiterDeviceItemIntent,
  canShowWaiterDeviceConfig,
  fetchWaiterOperationalCatalog,
  findWaiterProductForSessionItem,
  OPERATIONAL_CATALOG_CACHE_FRESH_MS,
} from '../utils/waiterOperation';
import {
  getOperationalCatalogSnapshot,
  requestOperationalCatalog,
} from '../utils/operationalCatalogLoader';
import {
  getReadySalonItemIds,
  getSalonConsumptionItems,
  getSalonPaymentRequest,
  getSalonReadyCount,
  getSalonReadyOrderLabels,
  getSalonUndeliveredItems,
  isSalonPaymentAwaitingCashier,
  isSalonPaymentCorrectionNeeded,
  SALON_PAYMENT_METHODS,
  salonPaymentMethodLabel,
} from '../utils/salonOperation';
import {
  calculateSelectedOptionsTotal,
  isPizzaCatalogItem,
  normalizeOptionGroupRole,
  normalizeOptionGroupLimits,
  resolveCatalogImageUrl,
  resolveDisplayOptionPrice,
  resolveSelectedOptionsForProduct,
} from '../utils/catalog';

const API_URL = getApiUrl();
const PRODUCTS_TIMEOUT_MS = 20000;

const readyBlink = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.2); transform: scale(1); }
  50% { box-shadow: 0 0 0 7px rgba(22, 163, 74, 0.38); transform: scale(1.025); }
`;

const launchGlow = keyframes`
  0%, 100% { box-shadow: 0 5px 14px rgba(25, 118, 210, 0.2); }
  50% { box-shadow: 0 7px 22px rgba(25, 118, 210, 0.38); }
`;

const createPaymentRequestId = () => (
  (typeof window !== 'undefined' ? window.crypto?.randomUUID?.() : null)
  || `waiter-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const STATUS_LABELS = {
  livre: 'Livre',
  ocupada: 'Ocupada',
  aguardando_preparo: 'Preparo',
  consumindo: 'Consumindo',
  aguardando_pagamento: 'Pagamento',
  fechada: 'Fechada',
  inativa: 'Inativa',
};

const STATUS_COLORS = {
  livre: { border: '#16a34a', bg: '#f0fdf4', color: '#166534' },
  ocupada: { border: '#f59e0b', bg: '#fffbeb', color: '#92400e' },
  aguardando_preparo: { border: '#ef4444', bg: '#fef2f2', color: '#991b1b' },
  consumindo: { border: '#2563eb', bg: '#eff6ff', color: '#1d4ed8' },
  aguardando_pagamento: { border: '#7c3aed', bg: '#f5f3ff', color: '#5b21b6' },
  fechada: { border: '#64748b', bg: '#f8fafc', color: '#334155' },
  inativa: { border: '#94a3b8', bg: '#f1f5f9', color: '#64748b' },
};

const ITEM_STATUS_LABELS = {
  pending: 'Não enviado',
  sent: 'Enviado',
  ready: 'Pronto',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
};

const formatCurrency = (value) => Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const buildProductKey = (product) => product.itemExternalCode || product.itemId || product.dbId || product.itemName;
const buildOptionKey = (option) => option.externalCode || option.optionId || option.name;
const productPrice = (product) => Number(product?.unitPrice ?? product?.itemPrice?.value ?? 0);

const parseLimit = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const getGroupLimits = (group) => {
  const limits = normalizeOptionGroupLimits(group);
  const min = Math.max(0, parseLimit(limits.minQuantity, 0));
  const maxRaw = parseLimit(limits.maxQuantity, Number.POSITIVE_INFINITY);
  const max = maxRaw <= 0 ? Number.POSITIVE_INFINITY : maxRaw;
  return { min, max };
};

const groupSessionItems = (items = []) => {
  const groups = {
    pending: [],
    sent: [],
    ready: [],
    delivered: [],
    cancelled: [],
  };
  items.forEach((item) => {
    const key = groups[item.status] ? item.status : 'sent';
    groups[key].push(item);
  });
  return groups;
};

const groupKey = (group = {}) => group.optionGroupId || group.groupId || group.id || group.name || 'group';

const findOptionInGroup = (group = {}, selectedOption = {}) => (
  (group.options || []).find((option) => {
    const selectedId = selectedOption.optionId || selectedOption.id || selectedOption.productId;
    const optionId = option.optionId || option.id || option.productId;
    return (
      (selectedId && optionId && String(selectedId) === String(optionId))
      || (selectedOption.externalCode && selectedOption.externalCode === option.externalCode)
      || (selectedOption.name && selectedOption.name === option.name)
    );
  }) || null
);

const buildSelectedOptionsState = (item = {}, product = {}) => {
  const state = {};
  const groups = product.itemOptionGroups || [];
  (item.options || []).forEach((selectedOption) => {
    const matchingGroup = groups.find((group) => (
      selectedOption.groupId === groupKey(group)
      || selectedOption.groupName === group.name
      || Boolean(findOptionInGroup(group, selectedOption))
    ));
    const groupId = matchingGroup ? groupKey(matchingGroup) : selectedOption.groupId || selectedOption.groupName || 'group';
    const originalOption = matchingGroup ? findOptionInGroup(matchingGroup, selectedOption) : null;
    const optionKey = buildOptionKey(originalOption || selectedOption);
    if (!optionKey) return;
    const groupType = matchingGroup ? normalizeOptionGroupRole(matchingGroup) : selectedOption.groupType || selectedOption.pizzaRole || '';
    if (!state[groupId]) state[groupId] = {};
    state[groupId][optionKey] = {
      ...(originalOption || {}),
      ...selectedOption,
      optionId: selectedOption.optionId || originalOption?.optionId || originalOption?.id || null,
      externalCode: selectedOption.externalCode || originalOption?.externalCode || null,
      name: selectedOption.name || originalOption?.name || '',
      quantity: Number(selectedOption.quantity || 1),
      groupId,
      groupName: matchingGroup?.name || selectedOption.groupName || '',
      groupType,
      pizzaRole: groupType,
    };
  });
  return state;
};

const buildFallbackOptionGroupsFromItem = (item = {}) => {
  const groups = new Map();
  (item.options || []).forEach((option) => {
    const groupId = option.groupId || option.groupName || 'group';
    if (!groups.has(groupId)) {
      groups.set(groupId, {
        optionGroupId: groupId,
        name: option.groupName || groupId,
        minQuantity: option.minQuantity ?? option.min ?? 0,
        maxQuantity: option.maxQuantity ?? option.max ?? 0,
        options: [],
      });
    }
    groups.get(groupId).options.push({
      optionId: option.optionId || option.id || null,
      externalCode: option.externalCode || null,
      name: option.name || '',
      price: { value: Number(option.price || 0) },
      contextualPrices: option.contextualPrices || {},
    });
  });
  return Array.from(groups.values());
};

const buildOptionGroupsForValidation = (product = {}) => (
  (product.itemOptionGroups || []).map((group) => {
    const limits = normalizeOptionGroupLimits(group);
    return {
      optionGroupId: groupKey(group),
      name: group.name || '',
      ...limits,
      options: (group.options || []).map((option) => ({
        optionId: option.optionId || option.id || option.productId || null,
        externalCode: option.externalCode || null,
        name: option.name || '',
      })),
    };
  })
);

const Garcom = ({ deviceMode = false }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tablesError, setTablesError] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [view, setView] = useState('dashboard');
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState({ open: false, table: null });
  const [openForm, setOpenForm] = useState({ customerName: '', peopleCount: '' });

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [catalogValidatedAt, setCatalogValidatedAt] = useState(0);
  const [productQuery, setProductQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [itemDialog, setItemDialog] = useState({ open: false, product: null });
  const [productPreview, setProductPreview] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [observations, setObservations] = useState('');
  const [selectedOptionsByGroup, setSelectedOptionsByGroup] = useState({});
  const [showOptionalGroups, setShowOptionalGroups] = useState(true);
  const [itemError, setItemError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [cancelDialog, setCancelDialog] = useState({ open: false, item: null });
  const [cancelReason, setCancelReason] = useState('');
  const [consumptionDialogOpen, setConsumptionDialogOpen] = useState(false);
  const [paymentRequestDialog, setPaymentRequestDialog] = useState({ open: false, requestId: '' });
  const [paymentRequestMethod, setPaymentRequestMethod] = useState('PIX');
  const [paymentRequestError, setPaymentRequestError] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const readyItemIdsRef = useRef(null);
  const audioContextRef = useRef(null);
  const readyAlertRef = useRef(null);
  const productsRequestIdRef = useRef(0);
  const productsAbortControllerRef = useRef(null);

  const notify = useCallback((message, severity = 'success') => {
    setToast({ open: true, message, severity });
  }, []);

  const unlockReadyAudio = useCallback(() => {
    if (audioContextRef.current || typeof window === 'undefined') return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    try {
      audioContextRef.current = new AudioContext();
      audioContextRef.current.resume?.().catch(() => {});
    } catch {
      audioContextRef.current = null;
    }
  }, []);

  const stopReadyAlert = useCallback((tableId = null) => {
    const active = readyAlertRef.current;
    if (!active || (tableId !== null && !active.tableIds.some((id) => Number(id) === Number(tableId)))) return;
    if (active.timeoutId) window.clearTimeout(active.timeoutId);
    (active.oscillators || []).forEach((oscillator) => {
      try { oscillator.stop(); } catch { /* Oscilador já finalizado. */ }
      try { oscillator.disconnect(); } catch { /* Sem ação. */ }
    });
    (active.gains || []).forEach((gain) => {
      try { gain.disconnect(); } catch { /* Sem ação. */ }
    });
    readyAlertRef.current = null;
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(0);
  }, []);

  const playReadyAlert = useCallback((tableIds = []) => {
    stopReadyAlert();
    const context = audioContextRef.current;
    const oscillators = [];
    const gains = [];
    if (context) {
      try {
        context.resume?.().catch(() => {});
        const startAt = context.currentTime;
        for (let burst = 0; burst < 5; burst += 0.8) {
          [0, 0.18, 0.36].forEach((offset, index) => {
            const oscillator = context.createOscillator();
            const gain = context.createGain();
            const toneAt = startAt + burst + offset;
            oscillator.type = 'square';
            oscillator.frequency.value = index === 1 ? 1040 : 820;
            gain.gain.setValueAtTime(0.0001, toneAt);
            gain.gain.exponentialRampToValueAtTime(0.42, toneAt + 0.015);
            gain.gain.exponentialRampToValueAtTime(0.0001, toneAt + 0.13);
            oscillator.connect(gain);
            gain.connect(context.destination);
            oscillator.start(toneAt);
            oscillator.stop(toneAt + 0.15);
            oscillators.push(oscillator);
            gains.push(gain);
          });
        }
      } catch {
        // O alerta visual continua ativo quando o navegador bloqueia áudio.
      }
    }
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate([300, 120, 300, 120, 500, 300, 300, 120, 300, 120, 500, 300, 300, 120, 300]);
    }
    const timeoutId = window.setTimeout(() => stopReadyAlert(), 5000);
    readyAlertRef.current = { tableIds: [...new Set(tableIds.filter(Boolean))], oscillators, gains, timeoutId };
  }, [stopReadyAlert]);

  const salonFetch = useCallback(async (primaryPath, devicePath, options = {}) => {
    const response = deviceMode
      ? await waiterDeviceFetch(`/api/salon/device${devicePath}`, options)
      : await apiFetch(`${API_URL}/api/salon${primaryPath}`, options);
    if (deviceMode && response.status === 401) {
      navigate('/garcom/acesso', { replace: true });
    }
    return response;
  }, [deviceMode, navigate]);

  const loadTables = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setTablesLoading(true);
      setTablesError('');
    }
    try {
      const response = await salonFetch('/tables?activeOnly=true', '/tables?activeOnly=true');
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao carregar mesas.');
      const nextTables = data.tables || [];
      const nextReadyIds = getReadySalonItemIds(nextTables);
      if (readyItemIdsRef.current) {
        const previousIds = readyItemIdsRef.current;
        const newReadyIds = nextReadyIds.filter((id) => !previousIds.has(id));
        if (newReadyIds.length > 0) {
          const newReadySet = new Set(newReadyIds);
          const readyTables = nextTables.filter((table) => (
            getReadySalonItemIds([table]).some((id) => newReadySet.has(id))
          ));
          playReadyAlert(readyTables.map((table) => table.id));
          const readyTable = readyTables[0];
          notify(`Pedido pronto${readyTable?.number ? ` na mesa ${readyTable.number}` : ''}.`, 'success');
        }
      }
      readyItemIdsRef.current = new Set(nextReadyIds);
      setTables(nextTables);
      if (selectedTable?.id) {
        const currentTable = nextTables.find((table) => Number(table.id) === Number(selectedTable.id));
        if (currentTable) setSelectedTable(currentTable);
        if (
          view === 'table'
          && selectedSession?.id
          && Number(currentTable?.activeSession?.id) !== Number(selectedSession.id)
        ) {
          setView('dashboard');
          setSelectedTable(null);
          setSelectedSession(null);
          setConsumptionDialogOpen(false);
          setPaymentRequestDialog({ open: false, requestId: '' });
          notify('A mesa foi fechada e está disponível para o próximo atendimento.');
        }
      }
    } catch (err) {
      if (!silent) setTablesError(err.message);
    } finally {
      if (!silent) setTablesLoading(false);
    }
  }, [notify, playReadyAlert, salonFetch, selectedSession?.id, selectedTable?.id, view]);

  const loadSession = useCallback(async (sessionId, { silent = false } = {}) => {
    if (!sessionId) return;
    if (!silent) setSessionLoading(true);
    try {
      const response = await salonFetch(`/sessions/${sessionId}`, `/sessions/${sessionId}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao carregar comanda.');
      setSelectedSession(data.session);
    } catch (err) {
      if (!silent) notify(err.message, 'error');
    } finally {
      if (!silent) setSessionLoading(false);
    }
  }, [notify, salonFetch]);

  const loadProducts = useCallback(async ({ force = false } = {}) => {
    const requestId = productsRequestIdRef.current + 1;
    productsRequestIdRef.current = requestId;
    productsAbortControllerRef.current?.abort();
    const ownerSession = deviceMode ? null : getCatalogStorageSession();
    const ownerIsCurrent = () => deviceMode || isCatalogStorageSessionCurrent(ownerSession);
    const cachedSnapshot = deviceMode ? null : getOperationalCatalogSnapshot({ session: ownerSession });
    const hasFreshCache = !force && cachedSnapshot?.isFresh === true && Array.isArray(cachedSnapshot.products);
    if (Array.isArray(cachedSnapshot?.products)) setProducts(cachedSnapshot.products);
    setProductsLoading(true);
    setProductsLoaded(hasFreshCache);
    setCatalogValidatedAt(hasFreshCache ? Number(cachedSnapshot.cachedAt) : 0);
    setProductsError('');
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    productsAbortControllerRef.current = deviceMode ? controller : null;
    try {
      const result = deviceMode
        ? await fetchWaiterOperationalCatalog(
          (signal) => salonFetch('/catalog', '/catalog', signal ? { signal } : {}),
          { timeoutMs: PRODUCTS_TIMEOUT_MS, controller }
        ).then((catalog) => ({
          products: adaptWaiterDeviceCatalog(catalog).map((product) => ({
            ...product,
            imageUrl: resolveCatalogImageUrl(product) || product.imageUrl || '',
          })),
          validatedAt: Date.now(),
        }))
        : await requestOperationalCatalog({
          session: ownerSession,
          timeoutMs: PRODUCTS_TIMEOUT_MS,
          fetchCatalog: (signal, metadata) => salonFetch('/catalog', '/catalog', {
            ...(signal ? { signal } : {}),
            ...(Object.keys(metadata.headers || {}).length ? { headers: metadata.headers } : {}),
          }),
        });
      if (productsRequestIdRef.current !== requestId) return;
      if (!ownerIsCurrent()) {
        setProducts([]);
        setProductsLoaded(false);
        setCatalogValidatedAt(0);
        return;
      }
      setProducts(result.products);
      setProductsLoaded(true);
      setCatalogValidatedAt(result.validatedAt);
    } catch (err) {
      if (productsRequestIdRef.current !== requestId) return;
      if (!ownerIsCurrent()) {
        setProducts([]);
        setProductsLoaded(false);
        setCatalogValidatedAt(0);
        return;
      }
      setProductsError(err.message);
      if (!Array.isArray(cachedSnapshot?.products)) setProducts([]);
      setProductsLoaded(hasFreshCache);
      setCatalogValidatedAt(hasFreshCache ? Number(cachedSnapshot.cachedAt) : 0);
    } finally {
      if (productsRequestIdRef.current === requestId) {
        productsAbortControllerRef.current = null;
        setProductsLoading(false);
      }
    }
  }, [deviceMode, salonFetch]);

  useEffect(() => {
    loadTables();
    loadProducts();
  }, [loadProducts, loadTables]);

  useEffect(() => {
    if (!productsLoaded || !catalogValidatedAt) return undefined;
    const remaining = OPERATIONAL_CATALOG_CACHE_FRESH_MS - (Date.now() - catalogValidatedAt);
    if (remaining <= 0) {
      setProductsLoaded(false);
      setCatalogValidatedAt(0);
      loadProducts({ force: true });
      return undefined;
    }
    const timeout = setTimeout(() => {
      setProductsLoaded(false);
      setCatalogValidatedAt(0);
      loadProducts({ force: true });
    }, remaining);
    return () => clearTimeout(timeout);
  }, [catalogValidatedAt, loadProducts, productsLoaded]);

  useEffect(() => () => {
    productsRequestIdRef.current += 1;
    productsAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('pointerdown', unlockReadyAudio, { once: true });
    window.addEventListener('keydown', unlockReadyAudio, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlockReadyAudio);
      window.removeEventListener('keydown', unlockReadyAudio);
      stopReadyAlert();
    };
  }, [stopReadyAlert, unlockReadyAudio]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadTables({ silent: true });
      if (view === 'table' && selectedSession?.id) loadSession(selectedSession.id, { silent: true });
    }, 5000);
    return () => clearInterval(interval);
  }, [loadSession, loadTables, selectedSession?.id, view]);

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((product) => set.add(product.categoryName || 'Sem categoria'));
    return ['Todos', ...Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'))];
  }, [products]);

  const filteredTables = useMemo(() => {
    const query = tableSearch.trim().toLowerCase();
    if (!query) return tables;
    return tables.filter((table) => (
      String(table.number || '').toLowerCase().includes(query) ||
      String(table.name || '').toLowerCase().includes(query) ||
      String(STATUS_LABELS[table.status] || table.status || '').toLowerCase().includes(query)
    ));
  }, [tableSearch, tables]);

  const filteredProducts = useMemo(() => {
    const query = productQuery.trim().toLowerCase();
    return products.filter((product) => {
      const category = product.categoryName || 'Sem categoria';
      if (selectedCategory !== 'Todos' && category !== selectedCategory) return false;
      if (!query) return true;
      return (
        String(product.itemName || '').toLowerCase().includes(query) ||
        String(product.itemDescription || '').toLowerCase().includes(query) ||
        String(category).toLowerCase().includes(query)
      );
    });
  }, [productQuery, products, selectedCategory]);

  const sessionItems = useMemo(() => groupSessionItems(selectedSession?.items || []), [selectedSession?.items]);
  const pendingCount = sessionItems.pending.length;
  const undeliveredCount = getSalonUndeliveredItems(selectedSession).length;

  const handleTableClick = async (table) => {
    if (!table.active || table.status === 'inativa') return;
    unlockReadyAudio();
    if (getSalonReadyCount(table) > 0) stopReadyAlert(table.id);
    setSelectedTable(table);
    if (table.activeSession?.id) {
      setView('table');
      await Promise.all([
        loadSession(table.activeSession.id),
        productsLoaded ? Promise.resolve() : loadProducts(),
      ]);
      return;
    }
    setSelectedSession(null);
    setOpenForm({ customerName: '', peopleCount: '' });
    setOpenDialog({ open: true, table });
  };

  const openSession = async () => {
    const table = openDialog.table;
    if (!table) return;
    setActionLoading(true);
    try {
      const response = await salonFetch(`/tables/${table.id}/open`, `/tables/${table.id}/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(openForm),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao abrir comanda.');
      setOpenDialog({ open: false, table: null });
      setSelectedSession(data.session);
      setView('table');
      notify(`Mesa ${table.number} aberta.`);
      await Promise.all([loadTables(), productsLoaded ? Promise.resolve() : loadProducts()]);
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const returnToDashboard = () => {
    setView('dashboard');
    setSelectedTable(null);
    setSelectedSession(null);
    setConsumptionDialogOpen(false);
  };

  const openItemDialog = (product) => {
    if (!productsLoaded) {
      notify('Aguarde a atualização do cardápio antes de lançar itens.', 'warning');
      return;
    }
    if (!selectedSession?.id || selectedSession.status !== 'aberta') {
      notify('Abra uma mesa disponível antes de lançar itens.', 'warning');
      return;
    }
    setItemDialog({ open: true, product, mode: 'add', item: null });
    setQuantity(1);
    setObservations('');
    setSelectedOptionsByGroup({});
    setShowOptionalGroups(true);
    setItemError('');
  };

  const launchProductFromPreview = () => {
    const product = productPreview;
    setProductPreview(null);
    if (product) openItemDialog(product);
  };

  const openEditItemDialog = (item) => {
    if (!productsLoaded) {
      notify('Aguarde a atualização do cardápio antes de alterar itens.', 'warning');
      return;
    }
    if (item.status !== 'pending') {
      notify('Este item já foi enviado para a cozinha. Para corrigir, cancele o item e lance novamente.', 'warning');
      return;
    }
    const product = findWaiterProductForSessionItem(item, products) || {
      dbId: item.productId ?? item.productDbId ?? null,
      productId: item.productId ?? item.productDbId ?? null,
      itemExternalCode: item.externalCode || '',
      itemName: item.name || '',
      itemDescription: item.description || '',
      imageUrl: item.imageUrl || '',
      unitPrice: item.unitPrice || 0,
      itemPrice: { value: item.unitPrice || 0 },
      itemOptionGroups: buildFallbackOptionGroupsFromItem(item),
    };
    setItemDialog({ open: true, product, mode: 'edit', item });
    setQuantity(Math.max(1, Number(item.quantity || 1)));
    setObservations(item.observations || '');
    setSelectedOptionsByGroup(buildSelectedOptionsState(item, product));
    setShowOptionalGroups(true);
    setItemError('');
  };

  const closeItemDialog = () => {
    setItemDialog({ open: false, product: null, mode: 'add', item: null });
    setItemError('');
  };

  const changeDialogProduct = (productKey) => {
    const nextProduct = products.find((product) => String(buildProductKey(product)) === String(productKey));
    if (!nextProduct) return;
    setItemDialog((prev) => ({ ...prev, product: nextProduct }));
    setSelectedOptionsByGroup({});
    setItemError('');
  };

  const setOptionQuantity = useCallback((group, option, nextQuantity) => {
    const groupId = groupKey(group);
    const optionKey = buildOptionKey(option);
    if (!optionKey) return;
    const { max } = getGroupLimits(group);
    const limits = normalizeOptionGroupLimits(group);
    const groupType = normalizeOptionGroupRole(group);
    setSelectedOptionsByGroup((prev) => {
      const currentGroup = prev[groupId] || {};
      const currentTotal = Object.values(currentGroup).reduce((sum, opt) => sum + Number(opt.quantity || 0), 0);
      const currentOptionQty = Number(currentGroup[optionKey]?.quantity || 0);
      const nextQty = Math.max(0, Number(nextQuantity || 0));
      const nextTotal = currentTotal - currentOptionQty + nextQty;
      const shouldReplace = Number.isFinite(max) && max === 1 && nextQty > 0;
      if (!shouldReplace && nextTotal > max) return prev;

      const nextGroup = shouldReplace ? {} : { ...currentGroup };
      if (nextQty <= 0) {
        delete nextGroup[optionKey];
      } else {
        nextGroup[optionKey] = {
          optionId: option.optionId || null,
          externalCode: option.externalCode || null,
          name: option.name,
          description: option.description || '',
          imageUrl: resolveCatalogImageUrl(option),
          imagePath: option.imagePath || '',
          logosUrls: Array.isArray(option.logosUrls) ? option.logosUrls : [],
          quantity: nextQty,
          price: Number(option.price?.value || 0),
          contextualPrices: option.contextualPrices || {},
          groupId,
          groupName: group.name || '',
          groupType,
          pizzaRole: groupType,
          minQuantity: limits.minQuantity,
          maxQuantity: limits.maxQuantity,
        };
      }
      return { ...prev, [groupId]: nextGroup };
    });
  }, []);

  const selectedOptions = useMemo(() => {
    const product = itemDialog.product;
    if (!product) return [];
    return resolveSelectedOptionsForProduct(product, selectedOptionsByGroup);
  }, [itemDialog.product, selectedOptionsByGroup]);

  const validateOptions = useCallback(() => {
    const product = itemDialog.product;
    for (const group of product?.itemOptionGroups || []) {
      if (!Array.isArray(group.options) || group.options.length === 0) continue;
      const groupId = groupKey(group);
      const { min, max } = getGroupLimits(group);
      const count = Object.values(selectedOptionsByGroup[groupId] || {}).reduce((sum, option) => sum + Number(option.quantity || 0), 0);
      if (count < min) return `Escolha pelo menos ${min} opção(ões) em "${group.name}".`;
      if (count > max) return `Escolha no máximo ${max} opção(ões) em "${group.name}".`;
    }
    return '';
  }, [itemDialog.product, selectedOptionsByGroup]);

  const saveItemToSession = async () => {
    const product = itemDialog.product;
    if (!product || !selectedSession?.id || !productsLoaded) return;
    const validation = validateOptions();
    if (validation) {
      setItemError(validation);
      return;
    }
    const qty = Math.max(1, Number(quantity || 1));
    const editingItemId = itemDialog.mode === 'edit' ? itemDialog.item?.id : null;
    const deviceIntent = buildWaiterDeviceItemIntent(product, qty, observations, selectedOptions);
    const baseUnitPrice = productPrice(product);
    const optionsTotal = calculateSelectedOptionsTotal(product, selectedOptions);
    const unitPrice = baseUnitPrice + optionsTotal;
    // Keep the administrative payload compatible during independent frontend/backend rollouts.
    // The current backend ignores these display/price fields and recalculates from fresh catalog data.
    const requestPayload = deviceMode ? deviceIntent : {
      ...deviceIntent,
      productDbId: product.dbId || null,
      externalCode: product.itemExternalCode || null,
      name: product.itemName,
      description: product.itemDescription || '',
      imageUrl: product.imageUrl || '',
      unitPrice,
      totalPrice: unitPrice * qty,
      optionGroups: buildOptionGroupsForValidation(product),
      options: selectedOptions.map((option) => ({
        ...option,
        groupName: option.groupName || '',
        groupType: option.groupType || option.pizzaRole || '',
      })),
    };
    setActionLoading(true);
    try {
      const primaryPath = editingItemId
        ? `/sessions/${selectedSession.id}/items/${editingItemId}`
        : `/sessions/${selectedSession.id}/items`;
      const response = await salonFetch(
        primaryPath,
        primaryPath,
        {
          method: editingItemId ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            editingItemId
              ? (deviceMode ? { item: requestPayload } : requestPayload)
              : { items: [requestPayload] }
          ),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || (editingItemId ? 'Falha ao alterar item.' : 'Falha ao lançar item.'));
      setSelectedSession(data.session);
      closeItemDialog();
      notify(editingItemId ? 'Item alterado na comanda.' : 'Item lançado na comanda.');
      await loadTables();
    } catch (err) {
      setItemError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const addItemToSession = saveItemToSession;

  const sendToKitchen = async () => {
    if (!selectedSession?.id || pendingCount === 0) return;
    setActionLoading(true);
    try {
      const response = await salonFetch(
        `/sessions/${selectedSession.id}/send-to-kitchen`,
        `/sessions/${selectedSession.id}/send-to-kitchen`,
        { method: 'POST' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao enviar para cozinha.');
      setSelectedSession(data.session);
      notify(`Rodada enviada: ${data.order?.displayId || data.order?.orderId || 'pedido de salão'}.`);
      await loadTables();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openPaymentRequest = () => {
    if (!selectedSession?.id) return;
    const currentRequest = getSalonPaymentRequest(selectedSession);
    setPaymentRequestMethod(currentRequest?.method || 'PIX');
    setPaymentRequestError('');
    setPaymentRequestDialog({ open: true, requestId: createPaymentRequestId() });
  };

  const submitPaymentRequest = async () => {
    if (!selectedSession?.id) return;
    const currentRequest = getSalonPaymentRequest(selectedSession);
    setActionLoading(true);
    setPaymentRequestError('');
    try {
      const response = await salonFetch(
        `/sessions/${selectedSession.id}/payment-request`,
        `/sessions/${selectedSession.id}/payment-request`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            method: paymentRequestMethod,
            requestId: paymentRequestDialog.requestId,
            ...(isSalonPaymentCorrectionNeeded(selectedSession)
              ? { revision: currentRequest?.revision }
              : null),
          }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao enviar pagamento para conferência.');
      setSelectedSession(data.session);
      setPaymentRequestDialog({ open: false, requestId: '' });
      notify('Pagamento enviado para conferência do caixa.');
      await loadTables();
    } catch (err) {
      setPaymentRequestError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const cancelItem = async () => {
    if (!selectedSession?.id || !cancelDialog.item) return;
    if (!cancelReason.trim()) {
      notify('Informe o motivo do cancelamento.', 'warning');
      return;
    }
    setActionLoading(true);
    try {
      const response = await salonFetch(
        `/sessions/${selectedSession.id}/cancel-item`,
        `/sessions/${selectedSession.id}/items/${cancelDialog.item.id}/cancel`,
        {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deviceMode
          ? { reason: cancelReason }
          : { itemId: cancelDialog.item.id, reason: cancelReason }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao cancelar item.');
      setSelectedSession(data.session);
      setCancelDialog({ open: false, item: null });
      setCancelReason('');
      notify('Item cancelado.');
      await loadTables();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const deliverOrder = async (orderId) => {
    if (!orderId) return;
    setActionLoading(true);
    try {
      const response = await salonFetch(`/orders/${orderId}/deliver`, `/orders/${orderId}/deliver`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao marcar pedido como entregue.');
      if (data.session) setSelectedSession(data.session);
      notify('Pedido entregue na mesa.');
      await loadTables();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const changeEstablishment = () => {
    clearWaiterDeviceSession();
    navigate('/garcom/acesso', { replace: true });
  };

  const renderItemRow = (item, allowCancel, allowDeliver = false) => (
    <Paper
      key={item.id}
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: item.status === 'cancelled' ? '#f8fafc' : item.status === 'delivered' ? '#f0fdf4' : '#fff',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={900}>{item.quantity}x {item.name}</Typography>
          {item.options?.length ? (
            <Typography variant="caption" color="text.secondary" display="block">
              + {item.options.map((option) => `${option.quantity || 1}x ${option.name}`).join(', ')}
            </Typography>
          ) : null}
          {item.observations ? <Typography variant="caption" display="block">Obs: {item.observations}</Typography> : null}
          <Typography variant="caption" color="text.secondary" display="block">
            {ITEM_STATUS_LABELS[item.status] || item.status} • {item.waiterName || 'garçom'} • {new Date(item.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </Typography>
          {item.orderDisplayId ? (
            <Chip size="small" color={item.status === 'ready' ? 'success' : 'default'} label={`Pedido #${item.orderDisplayId}`} sx={{ mt: 0.5 }} />
          ) : null}
        </Box>
        <Stack direction="row" alignItems="center" gap={1} justifyContent="space-between">
          <Typography fontWeight={900}>{formatCurrency(item.totalPrice)}</Typography>
          {item.status === 'pending' && selectedSession?.status === 'aberta' ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => openEditItemDialog(item)}
            >
              Editar
            </Button>
          ) : null}
          {allowCancel ? (
            <Button size="small" color="error" variant="outlined" onClick={() => setCancelDialog({ open: true, item })}>
              Cancelar
            </Button>
          ) : null}
          {allowDeliver ? (
            <Button
              size="small"
              color="success"
              variant="contained"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() => deliverOrder(item.orderId)}
              disabled={actionLoading}
            >
              Entregue
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: { xs: 1.5, md: 2 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} gap={1.5} sx={{ mb: 1.5 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <PageMenuButton />
            <Typography variant="h4" sx={{ fontWeight: 900 }}>Garçom</Typography>
          </Stack>
          <Typography color="text.secondary">
            {deviceMode
              ? `${getWaiterDeviceSession()?.company?.name || 'Estabelecimento'} • Mesas, lançamentos e rodadas de cozinha.`
              : 'Mesas, lançamentos e rodadas de cozinha.'}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {deviceMode ? (
            <Button variant="outlined" color="inherit" startIcon={<LogoutRoundedIcon />} onClick={changeEstablishment}>
              Trocar estabelecimento
            </Button>
          ) : null}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { loadTables(); if (view === 'table') loadProducts({ force: true }); }} disabled={tablesLoading || productsLoading}>
            Atualizar
          </Button>
          {view === 'table' ? (
            <Button variant="contained" startIcon={<SendIcon />} onClick={sendToKitchen} disabled={!selectedSession || pendingCount === 0 || actionLoading}>
              Enviar para cozinha
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {(tablesError || productsError) ? (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {tablesError ? <Alert severity="error">{tablesError}</Alert> : null}
          {productsError ? (
            <Alert
              severity="warning"
              action={<Button color="inherit" size="small" onClick={() => loadProducts({ force: true })} disabled={productsLoading}>Tentar novamente</Button>}
            >
              {productsError}
            </Alert>
          ) : null}
        </Stack>
      ) : null}

      {canShowWaiterDeviceConfig(auth.user, deviceMode) ? <WaiterDeviceConfig /> : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: view === 'dashboard' ? '1fr' : '0.85fr 1.15fr' }, gap: 2 }}>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: view === 'dashboard' ? 'block' : 'none' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
              <TextField
                value={tableSearch}
                onChange={(event) => setTableSearch(event.target.value)}
                placeholder="Buscar mesa"
                size="small"
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              />
              {tablesLoading ? <CircularProgress size={24} /> : <Chip label={`${filteredTables.length} mesa(s)`} />}
            </Stack>
            <Grid container spacing={1.25}>
              {filteredTables.map((table) => {
                const colors = STATUS_COLORS[table.status] || STATUS_COLORS.livre;
                const selected = Number(selectedTable?.id) === Number(table.id);
                const readyCount = getSalonReadyCount(table);
                const readyOrders = getSalonReadyOrderLabels(table);
                const correctionNeeded = isSalonPaymentCorrectionNeeded(table.activeSession);
                return (
                  <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={table.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderColor: selected ? '#111827' : colors.border,
                        borderWidth: selected ? 2 : 1,
                        bgcolor: colors.bg,
                        borderRadius: 2,
                        animation: readyCount > 0 ? `${readyBlink} 1.15s ease-in-out infinite` : 'none',
                        '@media (prefers-reduced-motion: reduce)': readyCount > 0 ? {
                          animation: 'none',
                          borderWidth: 2,
                          boxShadow: '0 0 0 4px rgba(37, 99, 235, 0.2)',
                        } : {},
                      }}
                    >
                      <CardActionArea onClick={() => handleTableClick(table)} disabled={!table.active || table.status === 'inativa'}>
                        <CardContent sx={{ minHeight: 128, display: 'grid', placeItems: 'center', textAlign: 'center', p: 1.5 }}>
                          <Badge color="error" badgeContent={readyOrders.length || readyCount || 0}>
                            <Avatar sx={{ width: 52, height: 52, bgcolor: colors.color, mb: 1 }}>
                              <EventSeatIcon />
                            </Avatar>
                          </Badge>
                          <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1 }}>Mesa {table.number}</Typography>
                          {table.name ? <Typography variant="caption" noWrap sx={{ width: '100%' }}>{table.name}</Typography> : null}
                          <Chip size="small" label={STATUS_LABELS[table.status] || table.status} sx={{ mt: 1, color: colors.color, borderColor: colors.border }} variant="outlined" />
                          {readyCount > 0 ? (
                            <Stack spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
                              <Chip size="small" color="success" label="Buscar na cozinha" />
                              {readyOrders.map((label) => (
                                <Typography key={label} variant="caption" color="success.dark" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                                  {label}
                                </Typography>
                              ))}
                            </Stack>
                          ) : null}
                          {correctionNeeded ? <Chip size="small" color="warning" label="Corrigir pagamento" sx={{ mt: 0.75 }} /> : null}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>

          <Paper data-testid="waiter-table-detail" variant="outlined" sx={{ p: 2, borderRadius: 2, position: { lg: 'sticky' }, top: { lg: 16 }, display: view === 'table' ? 'block' : 'none' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={returnToDashboard} sx={{ mb: 1 }}>
              Todas as mesas
            </Button>
            {!selectedSession ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">Selecione uma mesa</Typography>
              </Box>
            ) : (
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>Mesa {selectedSession.tableNumber}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Comanda #{selectedSession.id} • {selectedSession.waiterName || 'garçom'}
                    </Typography>
                  </Box>
                  <Chip color={selectedSession.status === 'aguardando_pagamento' ? 'secondary' : 'primary'} label={selectedSession.status === 'aguardando_pagamento' ? 'Pagamento' : 'Aberta'} />
                </Stack>
                {sessionLoading ? <LinearProgress /> : null}
                {isSalonPaymentCorrectionNeeded(selectedSession) ? (
                  <Alert severity="warning">
                    O caixa devolveu o pagamento para correção.
                    {getSalonPaymentRequest(selectedSession)?.reviewReason ? ` Motivo: ${getSalonPaymentRequest(selectedSession).reviewReason}` : ''}
                  </Alert>
                ) : null}
                {undeliveredCount > 0 ? (
                  <Alert severity="info">Entregue ou cancele todos os itens antes de fechar a conta.</Alert>
                ) : null}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack spacing={0.75}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary">Não enviado</Typography>
                      <Typography fontWeight={900}>{formatCurrency(selectedSession.pendingSubtotal)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary">Enviado</Typography>
                      <Typography fontWeight={900}>{formatCurrency(selectedSession.sentSubtotal)}</Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontWeight={900}>Total parcial</Typography>
                      <Typography fontWeight={900} color="primary">{formatCurrency(selectedSession.total)}</Typography>
                    </Stack>
                  </Stack>
                </Paper>

                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Button fullWidth variant="contained" startIcon={<SendIcon />} onClick={sendToKitchen} disabled={pendingCount === 0 || selectedSession.status !== 'aberta' || actionLoading}>
                    Enviar {pendingCount || ''} para cozinha
                  </Button>
                  <Button fullWidth variant="outlined" startIcon={<ReceiptLongOutlinedIcon />} onClick={() => setConsumptionDialogOpen(true)}>
                    Consumo
                  </Button>
                  <Button fullWidth variant="outlined" startIcon={<PaymentsOutlinedIcon />} onClick={openPaymentRequest} disabled={!['aberta', 'aguardando_pagamento'].includes(selectedSession.status) || isSalonPaymentAwaitingCashier(selectedSession) || undeliveredCount > 0 || actionLoading}>
                    {isSalonPaymentAwaitingCashier(selectedSession)
                      ? 'Aguardando conferência'
                      : isSalonPaymentCorrectionNeeded(selectedSession) ? 'Corrigir pagamento' : 'Fechar conta'}
                  </Button>
                </Stack>

                {sessionItems.ready.length > 0 ? (
                  <Alert icon={<CheckCircleOutlineIcon fontSize="inherit" />} severity="success">
                    ENTREGAR NA MESA {selectedSession.tableNumber} • {sessionItems.ready.length} item(ns) pronto(s)
                    {selectedSession.waiterName ? ` • Garçom: ${selectedSession.waiterName}` : ''}.
                    <Typography component="div" variant="body2" sx={{ mt: 0.5, fontWeight: 900 }}>
                      {getSalonReadyOrderLabels(selectedSession).join(' • ')}
                    </Typography>
                  </Alert>
                ) : null}

                <Stack spacing={1}>
                  {sessionItems.pending.length > 0 ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.75 }}>Ainda não enviados</Typography>
                      <Stack spacing={0.75}>{sessionItems.pending.map((item) => renderItemRow(item, selectedSession.status === 'aberta'))}</Stack>
                    </Box>
                  ) : null}
                  {sessionItems.ready.length > 0 ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.75 }}>Prontos para entregar</Typography>
                      <Stack spacing={0.75}>{sessionItems.ready.map((item) => renderItemRow(item, false, Boolean(item.orderId)))}</Stack>
                    </Box>
                  ) : null}
                  {sessionItems.sent.length > 0 ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.75 }}>Enviados</Typography>
                      <Stack spacing={0.75}>{sessionItems.sent.map((item) => renderItemRow(item, false))}</Stack>
                    </Box>
                  ) : null}
                  {sessionItems.delivered.length > 0 ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.75 }}>Entregues</Typography>
                      <Stack spacing={0.75}>{sessionItems.delivered.map((item) => renderItemRow(item, false))}</Stack>
                    </Box>
                  ) : null}
                  {sessionItems.cancelled.length > 0 ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.75 }}>Cancelados</Typography>
                      <Stack spacing={0.75}>{sessionItems.cancelled.map((item) => renderItemRow(item, false))}</Stack>
                    </Box>
                  ) : null}
                </Stack>
              </Stack>
            )}
          </Paper>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: view === 'table' ? 'block' : 'none' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={1} sx={{ mb: 2 }}>
            <TextField
              value={productQuery}
              onChange={(event) => setProductQuery(event.target.value)}
              placeholder="Buscar produtos"
              size="small"
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <TextField select size="small" label="Categoria" value={selectedCategory} onChange={(event) => setSelectedCategory(event.target.value)} SelectProps={{ native: true }} sx={{ minWidth: 220 }}>
              {categories.map((category) => <option key={category} value={category}>{category}</option>)}
            </TextField>
          </Stack>
          {productsLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
          {!productsLoading && productsLoaded && filteredProducts.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>Nenhum produto encontrado para esta busca.</Alert>
          ) : null}
          <Grid container spacing={1.5}>
            {filteredProducts.map((product) => (
              <Grid size={{ xs: 12, sm: 6, xl: 4 }} key={buildProductKey(product)}>
                <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <ButtonBase
                    data-testid={`waiter-product-image-${buildProductKey(product)}`}
                    onClick={() => setProductPreview(product)}
                    aria-label={`Ver foto e descrição de ${product.itemName}`}
                    sx={{ width: '100%', display: 'block', position: 'relative', overflow: 'hidden' }}
                  >
                    {product.imageUrl ? (
                      <CardMedia
                        component="img"
                        loading="lazy"
                        image={resolveCatalogImageUrl(product.imageUrl)}
                        alt={product.itemName}
                        height="132"
                        sx={{ objectFit: 'cover', transition: 'transform 220ms ease', '.MuiButtonBase-root:hover &': { transform: 'scale(1.035)' } }}
                      />
                    ) : (
                      <Box sx={{ height: 132, display: 'grid', placeItems: 'center', bgcolor: '#f1f5f9' }}>
                        <LocalMallOutlinedIcon color="disabled" />
                      </Box>
                    )}
                    <Box sx={{ position: 'absolute', top: 8, right: 8, width: 34, height: 34, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,0.94)', color: 'primary.main', boxShadow: '0 3px 10px rgba(15, 23, 42, 0.18)' }}>
                      <ZoomInOutlinedIcon fontSize="small" />
                    </Box>
                  </ButtonBase>
                  <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Typography fontWeight={900} noWrap>{product.itemName}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{product.categoryName || 'Sem categoria'}</Typography>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5} sx={{ mt: 'auto', pt: 1.25 }}>
                      <Typography color="primary" fontWeight={900}>{formatCurrency(productPrice(product))}</Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => openItemDialog(product)}
                        disabled={!productsLoaded || !selectedSession || selectedSession.status !== 'aberta'}
                        sx={{
                          minWidth: 118,
                          minHeight: 42,
                          px: 2,
                          borderRadius: 999,
                          fontWeight: 900,
                          letterSpacing: 0.2,
                          animation: `${launchGlow} 2.4s ease-in-out infinite`,
                          transition: 'transform 160ms ease, box-shadow 160ms ease',
                          '&:hover': { transform: 'translateY(-2px)' },
                          '&:active': { transform: 'translateY(0) scale(0.98)' },
                          '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' },
                        }}
                      >
                        Lançar
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>

      <Dialog open={openDialog.open} onClose={() => setOpenDialog({ open: false, table: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Abrir mesa {openDialog.table?.number}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField label="Cliente (opcional)" value={openForm.customerName} onChange={(event) => setOpenForm((prev) => ({ ...prev, customerName: event.target.value }))} />
            <TextField label="Pessoas" type="number" value={openForm.peopleCount} onChange={(event) => setOpenForm((prev) => ({ ...prev, peopleCount: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog({ open: false, table: null })}>Cancelar</Button>
          <Button variant="contained" onClick={openSession} disabled={actionLoading}>Abrir</Button>
        </DialogActions>
      </Dialog>

      <Dialog data-testid="waiter-product-preview" open={Boolean(productPreview)} onClose={() => setProductPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 7 }}>
          {productPreview?.itemName || 'Detalhes do item'}
          <IconButton
            onClick={() => setProductPreview(null)}
            aria-label="Fechar detalhes do item"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 1.15fr) 0.85fr' }, gap: 2.5, alignItems: 'start' }}>
            {productPreview?.imageUrl ? (
              <Box
                component="img"
                src={resolveCatalogImageUrl(productPreview.imageUrl)}
                alt={productPreview.itemName || 'Foto do item'}
                sx={{ width: '100%', maxHeight: { xs: 340, md: 520 }, objectFit: 'contain', borderRadius: 2, bgcolor: '#f8fafc' }}
              />
            ) : (
              <Box sx={{ minHeight: 260, display: 'grid', placeItems: 'center', borderRadius: 2, bgcolor: '#f1f5f9' }}>
                <LocalMallOutlinedIcon color="disabled" sx={{ fontSize: 58 }} />
              </Box>
            )}
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight={800}>
                  {productPreview?.categoryName || 'Sem categoria'}
                </Typography>
                <Typography variant="h5" fontWeight={900}>{productPreview?.itemName}</Typography>
              </Box>
              <Typography color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.65 }}>
                {productPreview?.itemDescription || productPreview?.description || 'Este item ainda não possui descrição cadastrada.'}
              </Typography>
              <Divider />
              <Typography variant="h5" color="primary" fontWeight={900}>
                {formatCurrency(productPrice(productPreview || {}))}
              </Typography>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setProductPreview(null)}>Voltar</Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddCircleOutlineIcon />}
            onClick={launchProductFromPreview}
            disabled={!selectedSession || selectedSession.status !== 'aberta'}
            sx={{ borderRadius: 999, px: 3, fontWeight: 900 }}
          >
            Lançar na mesa
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={itemDialog.open} onClose={closeItemDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {itemDialog.mode === 'edit'
            ? 'Editar item da mesa'
            : isPizzaCatalogItem(itemDialog.product) ? `Montar ${itemDialog.product?.itemName || 'pizza'}` : itemDialog.product?.itemName || 'Lançar item'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {itemError ? <Alert severity="error">{itemError}</Alert> : null}
            {itemDialog.mode === 'edit' ? (
              <TextField
                select
                SelectProps={{ native: true }}
                label="Produto"
                value={itemDialog.product ? String(buildProductKey(itemDialog.product)) : ''}
                onChange={(event) => changeDialogProduct(event.target.value)}
              >
                {itemDialog.product && !products.some((product) => String(buildProductKey(product)) === String(buildProductKey(itemDialog.product))) ? (
                  <option value={String(buildProductKey(itemDialog.product))}>{itemDialog.product.itemName || itemDialog.product.name}</option>
                ) : null}
                {products.map((product) => (
                  <option key={buildProductKey(product)} value={String(buildProductKey(product))}>
                    {product.itemName} - {formatCurrency(productPrice(product))}
                  </option>
                ))}
              </TextField>
            ) : null}
            <Stack direction="row" alignItems="center" gap={1}>
              <IconButton onClick={() => setQuantity((value) => Math.max(1, value - 1))}><RemoveIcon /></IconButton>
              <Typography variant="h6" sx={{ minWidth: 36, textAlign: 'center', fontWeight: 900 }}>{quantity}</Typography>
              <IconButton onClick={() => setQuantity((value) => value + 1)}><AddIcon /></IconButton>
              <Typography color="text.secondary">unidade(s)</Typography>
            </Stack>
            <TextField label="Observação do item" value={observations} onChange={(event) => setObservations(event.target.value)} multiline minRows={2} />

            {(itemDialog.product?.itemOptionGroups || []).some((group) => Array.isArray(group.options) && group.options.length > 0) ? (
              <FormControlLabel
                control={<Switch checked={showOptionalGroups} onChange={(event) => setShowOptionalGroups(event.target.checked)} />}
                label={isPizzaCatalogItem(itemDialog.product) ? 'Opções da pizza' : 'Complementos'}
              />
            ) : null}

            {showOptionalGroups ? (
              <Stack spacing={1}>
                {(itemDialog.product?.itemOptionGroups || [])
                  .filter((group) => Array.isArray(group.options) && group.options.length > 0)
                  .map((group) => {
                    const groupId = groupKey(group);
                    const selectedMap = selectedOptionsByGroup[groupId] || {};
                    const selectedCount = Object.values(selectedMap).reduce((sum, option) => sum + Number(option.quantity || 0), 0);
                    const { min, max } = getGroupLimits(group);
                    const groupRole = normalizeOptionGroupRole(group);
                    const groupTitle = group.name || ({
                      SIZE: 'Tamanho',
                      FLAVOR: 'Sabores',
                      DOUGH: 'Massa',
                      EDGE: 'Borda',
                      CRUST: 'Massa',
                    }[groupRole] || 'Complementos');
                    const missingMin = selectedCount < min;
                    return (
                      <Paper key={groupId} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: missingMin ? 'error.main' : 'divider' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography fontWeight={900}>{groupTitle}</Typography>
                          <Chip size="small" color={missingMin ? 'error' : 'default'} label={min > 0 ? `${selectedCount}/${min} min.` : `Até ${Number.isFinite(max) ? max : '∞'}`} />
                        </Stack>
                        <List dense disablePadding>
                          {(group.options || []).map((option) => {
                            const optionKey = buildOptionKey(option);
                            const currentQty = Number(selectedMap[optionKey]?.quantity || 0);
                            const optionImage = resolveCatalogImageUrl(option);
                            const price = resolveDisplayOptionPrice(itemDialog.product, group, option, selectedOptionsByGroup);
                            const canIncrease = selectedCount < max;
                            return (
                              <ListItem
                                key={optionKey}
                                disableGutters
                                secondaryAction={
                                  <Stack direction="row" alignItems="center" gap={0.5}>
                                    <IconButton size="small" onClick={() => setOptionQuantity(group, option, currentQty - 1)} disabled={currentQty <= 0}>
                                      <RemoveIcon fontSize="small" />
                                    </IconButton>
                                    <Typography sx={{ minWidth: 18, textAlign: 'center', fontWeight: 900 }}>{currentQty}</Typography>
                                    <IconButton size="small" onClick={() => setOptionQuantity(group, option, currentQty + 1)} disabled={!canIncrease}>
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                }
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    variant="rounded"
                                    src={optionImage || undefined}
                                    imgProps={{ loading: 'lazy', referrerPolicy: 'no-referrer' }}
                                    sx={{
                                      width: 42,
                                      height: 42,
                                      bgcolor: '#e2e8f0',
                                      border: '1px solid #e2e8f0',
                                      '& img': { objectFit: 'cover' },
                                    }}
                                  >
                                    <LocalMallOutlinedIcon color="disabled" />
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={<Typography fontWeight={800}>{option.name}</Typography>}
                                  secondary={price > 0 ? `+ ${formatCurrency(price)}` : option.description || ''}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </Paper>
                    );
                  })}
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeItemDialog}>Cancelar</Button>
          <Button variant="contained" onClick={addItemToSession} disabled={actionLoading}>
            {itemDialog.mode === 'edit' ? 'Salvar alteração' : 'Lançar na mesa'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={consumptionDialogOpen} onClose={() => setConsumptionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Consumo da mesa {selectedSession?.tableNumber}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            {getSalonConsumptionItems(selectedSession).length ? (
              getSalonConsumptionItems(selectedSession).map((item) => (
                <Paper key={item.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Box>
                      <Typography fontWeight={900}>{item.quantity}x {item.name}</Typography>
                      {item.options?.length ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          + {item.options.map((option) => `${option.quantity || 1}x ${option.name}`).join(', ')}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" color="text.secondary">
                        {ITEM_STATUS_LABELS[item.status] || item.status}
                      </Typography>
                    </Box>
                    <Typography fontWeight={900}>{formatCurrency(item.totalPrice)}</Typography>
                  </Stack>
                </Paper>
              ))
            ) : (
              <Alert severity="info">Esta mesa ainda não possui consumo.</Alert>
            )}
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6" fontWeight={900}>Total</Typography>
              <Typography variant="h6" fontWeight={900} color="primary">{formatCurrency(selectedSession?.total)}</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setConsumptionDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={paymentRequestDialog.open}
        onClose={() => !actionLoading && setPaymentRequestDialog({ open: false, requestId: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Fechar conta da mesa {selectedSession?.tableNumber}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {paymentRequestError ? <Alert severity="error">{paymentRequestError}</Alert> : null}
            {isSalonPaymentCorrectionNeeded(selectedSession) ? (
              <Alert severity="warning">
                Revise a forma de pagamento e envie novamente ao caixa.
                {getSalonPaymentRequest(selectedSession)?.reviewReason ? ` Motivo: ${getSalonPaymentRequest(selectedSession).reviewReason}` : ''}
              </Alert>
            ) : null}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack spacing={0.75}>
                {getSalonConsumptionItems(selectedSession).map((item) => (
                  <Stack key={item.id} direction="row" justifyContent="space-between" gap={1}>
                    <Typography variant="body2">{item.quantity}x {item.name}</Typography>
                    <Typography variant="body2" fontWeight={800}>{formatCurrency(item.totalPrice)}</Typography>
                  </Stack>
                ))}
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontWeight={900}>Total a pagar</Typography>
                  <Typography fontWeight={900} color="primary">{formatCurrency(selectedSession?.total)}</Typography>
                </Stack>
              </Stack>
            </Paper>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Forma de pagamento recebida"
              value={paymentRequestMethod}
              onChange={(event) => setPaymentRequestMethod(event.target.value)}
              helperText="O caixa ainda precisará conferir e confirmar este pagamento."
            >
              {SALON_PAYMENT_METHODS.map((method) => (
                <option key={method.value} value={method.value}>{method.label}</option>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentRequestDialog({ open: false, requestId: '' })} disabled={actionLoading}>Cancelar</Button>
          <Button variant="contained" onClick={submitPaymentRequest} disabled={actionLoading}>
            {actionLoading ? 'Enviando…' : `Enviar ${salonPaymentMethodLabel(paymentRequestMethod)} ao caixa`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, item: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar item</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography>{cancelDialog.item?.name}</Typography>
            <TextField label="Motivo" value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} multiline minRows={2} required />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog({ open: false, item: null })}>Voltar</Button>
          <Button color="error" variant="contained" onClick={cancelItem} disabled={actionLoading}>Confirmar cancelamento</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Garcom;
